import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

export interface INotes {
    id?: number;
    title: string;
    description: string;
    created?: string | Date;
}

export const fetchNotes = createAsyncThunk("notes/getNotes", async (termId: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/notes`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const postNotes = createAsyncThunk(
    "notes/postNotes",
    async ({ termId, data }: { termId: number; data: INotes }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/notes`, data);
            return { data, responseId: response.data.result.insertId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const deleteNote = createAsyncThunk(
    "notes/deleteNote",
    async ({ termId, noteId }: { termId: number; noteId: number }, { rejectWithValue }) => {
        try {
            await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/notes/${noteId}`);
            return noteId;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface NotesState {
    notes: INotes[];
}

const initialState: NotesState = {
    notes: [],
};

export const notesSlice = createSlice({
    name: "notes",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchNotes.fulfilled, (state, action) => {
            state.notes = action.payload;
        });
        builder.addCase(fetchNotes.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(postNotes.fulfilled, (state, action) => {
            toast.success(`${t("RichEditor.CreateNoteSuccess")}`);
            state.notes.unshift({
                ...action.payload.data,
                id: action.payload.responseId,
                created: new Date().toString(),
            });
        });
        builder.addCase(postNotes.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteNote.fulfilled, (state, action) => {
            toast.success(`${t("RichEditor.DeleteNoteSuccess")}`);
            state.notes = state.notes.filter((note) => note.id != action.payload);
        });
        builder.addCase(deleteNote.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default notesSlice.reducer;
